/**
 * Tiles view — the aggregate metric grid under the hero.
 *
 * Renders ten `.tile` cards in a fixed order:
 *
 *   1. Maturity Index         → snapshot.maturity
 *   2. Total use cases        → snapshot.total
 *   3. In production          → snapshot.prod
 *   4. In development         → snapshot.dev
 *   5. In pipeline            → snapshot.pipe
 *   6. Revenue impact         → snapshot.revenueTotal   ("$…" or "—" when 0)
 *   7. Adoption               → snapshot.adoptionTotal  ("… users" or "—")
 *   8. AI tasks run           → snapshot.tasksTotal     ("… steps" or "—")
 *   9. Avg time to value      → snapshot.ttvAvg         ("… days" or "—")
 *  10. Avg productivity uplift→ snapshot.productivityAvg("… hrs"  or "—")
 *
 * DOM structure mirrors the prototype (`ai-maturity-index-portal_9.html`):
 *
 *   <div class="tiles">
 *     <div class="tile">
 *       <div class="k">Label</div>
 *       <div class="v">Value</div>
 *     </div>
 *     …
 *   </div>
 *
 * This module has no store subscription — the portal shell (task 7.7) drives
 * `render()` calls. Both views accept `snapshot === null` for the initial
 * boot state.
 *
 * Requirements: 10.1, 10.4, 10.6, 10.7.
 */
import type { IndexSnapshot } from "@aim/types";

/** Public surface of the tiles view. */
export interface TilesView {
  /** Mount the tiles container into `container`. Idempotent — safe to call twice. */
  mount(container: HTMLElement): void;
  /** Render (or re-render) the current snapshot. Safe to call before mount is a no-op. */
  render(snapshot: IndexSnapshot | null): void;
  /** Unmount and release cached element references. */
  destroy(): void;
}

/** Placeholder rendered when a metric has no meaningful value. */
const PLACEHOLDER = "—";

/** Fixed number of tiles rendered. Kept as a named constant for the tests. */
export const TILE_COUNT = 10;

/**
 * Local number formatter. Kept local to this module — no shared util file
 * exists yet (see task 7.3 acceptance criteria).
 */
function fmt(n: number): string {
  return Number(n).toLocaleString("en-US");
}

/** Format a USD amount, or "—" when 0. */
function fmtUSD(n: number): string {
  return n > 0 ? `$${fmt(n)}` : PLACEHOLDER;
}

/** Format an integer with a unit suffix, or "—" when 0. */
function fmtCount(n: number, unit: string): string {
  return n > 0 ? `${fmt(n)} ${unit}` : PLACEHOLDER;
}

/** Format a nullable average with a unit suffix, or "—" when null. */
function fmtAvg(n: number | null, unit: string): string {
  return n !== null ? `${fmt(n)} ${unit}` : PLACEHOLDER;
}

/**
 * Compute the ordered list of `{ label, value }` pairs from a snapshot.
 * Extracted as a pure function so the tests can lean on ordering directly.
 */
function tileDefsFor(
  snapshot: IndexSnapshot | null,
): ReadonlyArray<{ label: string; value: string }> {
  if (snapshot === null) {
    return [
      { label: "Maturity Index", value: PLACEHOLDER },
      { label: "Total use cases", value: PLACEHOLDER },
      { label: "In production", value: PLACEHOLDER },
      { label: "In development", value: PLACEHOLDER },
      { label: "In pipeline", value: PLACEHOLDER },
      { label: "Revenue impact", value: PLACEHOLDER },
      { label: "Adoption", value: PLACEHOLDER },
      { label: "AI tasks run", value: PLACEHOLDER },
      { label: "Avg time to value", value: PLACEHOLDER },
      { label: "Avg productivity uplift", value: PLACEHOLDER },
    ];
  }

  return [
    { label: "Maturity Index", value: fmt(snapshot.maturity) },
    { label: "Total use cases", value: fmt(snapshot.total) },
    { label: "In production", value: fmt(snapshot.prod) },
    { label: "In development", value: fmt(snapshot.dev) },
    { label: "In pipeline", value: fmt(snapshot.pipe) },
    { label: "Revenue impact", value: fmtUSD(snapshot.revenueTotal) },
    { label: "Adoption", value: fmtCount(snapshot.adoptionTotal, "users") },
    { label: "AI tasks run", value: fmtCount(snapshot.tasksTotal, "steps") },
    { label: "Avg time to value", value: fmtAvg(snapshot.ttvAvg, "days") },
    {
      label: "Avg productivity uplift",
      value: fmtAvg(snapshot.productivityAvg, "hrs"),
    },
  ];
}

/**
 * Build a single `.tile` element. Values are set via `textContent` so any
 * future string that carries user input remains safe from HTML injection.
 */
function buildTile(label: string, value: string): HTMLElement {
  const tile = document.createElement("div");
  tile.className = "tile";

  const k = document.createElement("div");
  k.className = "k";
  k.textContent = label;

  const v = document.createElement("div");
  v.className = "v";
  v.textContent = value;

  tile.append(k, v);
  return tile;
}

/** Factory — every call returns an independent view instance. */
export function createTilesView(): TilesView {
  let root: HTMLElement | null = null;

  return {
    mount(container: HTMLElement): void {
      // Idempotent: replace any previous contents (including a previous
      // mount of this view) with a fresh `.tiles` container.
      container.replaceChildren();
      root = document.createElement("div");
      root.className = "tiles";
      container.append(root);
    },

    render(snapshot: IndexSnapshot | null): void {
      if (root === null) {
        return;
      }
      // Rebuild children on every render — the tile set is small (10) and
      // fixed in shape, so diffing would add cost without benefit.
      const defs = tileDefsFor(snapshot);
      const tiles = defs.map((d) => buildTile(d.label, d.value));
      root.replaceChildren(...tiles);
    },

    destroy(): void {
      if (root !== null) {
        root.remove();
        root = null;
      }
    },
  };
}
